import { API_BASE, api } from "@/lib/apiClient";
import { userIsBuero, type CurrentUser } from "@/lib/auth";
import { stripGdRichTextMarkup } from "@/lib/gdRichText";

export type DokumentTyp =
  | "angebot"
  | "rechnung"
  | "lieferschein"
  | "auftragsbestaetigung"
  | "stundennachweis"
  | "stueckliste";

export type DokumentQuelle =
  | "geschaeftsdokument"
  | "stundennachweis"
  | "stueckliste"
  | "hero_customer_document";

export type TimesheetListStatus =
  | "draft"
  | "ready"
  | "signed"
  | "invoice_draft"
  | "invoice_created"
  | "invoice_paid"
  | "archived";

export type DokumentStatus =
  | "entwurf"
  | "erstellt"
  | "versendet"
  | "storniert"
  | "angenommen"
  | "abgelehnt"
  | "abgerechnet"
  | "geliefert";

export type PositionTyp = "artikel" | "leistung" | "titel" | "text" | "freitext" | "zwischensumme";

export interface ArtikelNachkalkulationJson {
  einkaufspreis_netto?: number;
  aufschlag_prozent?: number;
  /** Absoluter Aufschlag in Euro auf den EK, wenn aufschlag_modus = betrag. */
  aufschlag_betrag?: number;
  aufschlag_modus?: "prozent" | "betrag";
  preis_driver?: "vk" | "aufschlag";
  _gd_meta?: {
    interne_notiz?: string;
    struktur_titel_local_id?: string | null;
    struktur_titel_position_id?: number | null;
  };
}

export interface LeistungKalkulationJson {
  artikel_liste?: Array<{
    localId?: string;
    artikel_id?: number | null;
    ist_freier_artikel?: boolean;
    bezeichnung?: string;
    hero_nr?: string | null;
    einheit?: string | null;
    menge?: number;
    einzelpreis_netto?: number;
    einkaufspreis_netto?: number;
    aufschlag_prozent?: number;
    rabatt_prozent?: number;
    /** einzel = je Stück der Position, gesamt = einmal für die ganze Menge. */
    preisbezug?: "einzel" | "gesamt";
    interne_beschreibung?: string;
  }>;
  lohn_liste?: Array<{
    localId?: string;
    lohnart_id?: string;
    bezeichnung?: string;
    stunden?: number;
    stundensatz_netto?: number;
    /** einzel = Stunden/Satz je Stück, gesamt = einmal für die ganze Menge. */
    preisbezug?: "einzel" | "gesamt";
    interne_beschreibung?: string;
  }>;
  stunden?: number;
  stundensatz_netto?: number;
  pauschale_netto?: number;
  aufschlag_prozent?: number;
  vk_driver?: "kalkuliert" | "pauschale" | "manuell";
  _gd_meta?: ArtikelNachkalkulationJson["_gd_meta"];
}

export interface ArtikelQuelle {
  artikel_id: number;
  hero_nr?: string | null;
  bezeichnung: string;
  einheit?: string | null;
  einzelpreis_netto: number;
  einkaufspreis_netto?: number;
  aufschlag_prozent?: number;
  rabatt_prozent: number;
  preis_quelle?: "hero" | "datenbank";
  hero_preis_netto?: number;
  /** Abgelaufener DB-Preis: nur Orientierung, nicht in der Dokumentsumme. */
  preis_abgelaufen?: boolean;
  orientierungspreis_netto?: number | null;
  preis_gueltig_bis?: string | null;
  /** Interne Notizen / Hero-Langtext aus der Artikeldatenbank (Klartext). */
  interne_beschreibung?: string | null;
}

export interface GdLohnart {
  id: string;
  bezeichnung: string;
  stundensatz_netto: number;
  sort_index: number;
  sichtbarkeit?: GdLohnartSichtbarkeit;
}

export type GdLohnartSichtbarkeit = "alle" | "buero";

export const GD_LOHNART_SICHTBARKEIT_LABELS: Record<GdLohnartSichtbarkeit, string> = {
  alle: "Alle",
  buero: "Nur Büro",
};

export function normalizeGdLohnartSichtbarkeit(value?: string | null): GdLohnartSichtbarkeit {
  return value === "buero" ? "buero" : "alle";
}

export function filterGdLohnartenForUser(lohnarten: GdLohnart[], user: CurrentUser | null): GdLohnart[] {
  if (userIsBuero(user)) return lohnarten;
  return lohnarten.filter((item) => normalizeGdLohnartSichtbarkeit(item.sichtbarkeit) === "alle");
}

export interface DokumentPosition {
  id?: number;
  dokument_id?: number;
  position_nr?: number;
  sort_index: number;
  position_typ: PositionTyp;
  artikel_id?: number | null;
  ist_freier_artikel?: boolean;
  bezeichnung: string;
  beschreibung?: string | null;
  hero_nr?: string | null;
  einheit?: string | null;
  menge: number;
  einzelpreis_netto: number;
  rabatt_prozent: number;
  /** Absoluter Rabatt in Euro auf die Position, wenn rabatt_modus = betrag. */
  rabatt_betrag?: number;
  rabatt_modus?: "prozent" | "betrag";
  /** intern: nur reduzierter Preis. dokument: Rabatt steht auf der Rechnung. */
  rabatt_ausweis?: "intern" | "dokument";
  steuersatz_prozent: number;
  zeilen_netto?: number;
  zeilen_steuer?: number;
  zeilen_brutto?: number;
  quelle_position_id?: number | null;
  kalkulation_json?: LeistungKalkulationJson | ArtikelNachkalkulationJson | null;
}

export interface DokumentVerknuepfung {
  id: number;
  quell_dokument_id: number;
  ziel_dokument_id: number;
  verknuepfung_typ: string;
  quell_nummer?: string | null;
  quell_typ?: string | null;
  ziel_nummer?: string | null;
  ziel_typ?: string | null;
  created_by: string;
  created_at: string;
}

export type Rechnungsformat = "zugpferd" | "xrechnung";

export type RechnungArt = "teilrechnung" | "schlussrechnung" | "stornorechnung" | "vorkasse";
export type BillingReopenTarget = "teilrechnung" | "vorkasse";

export type Zahlungsstatus = "offen" | "teilbezahlt" | "bezahlt";
export type AbschlagModus = "prozent" | "festbetrag";

export interface DokumentZahlung {
  id: number;
  dokument_id: number;
  betrag: number;
  bezahlt_am: string;
  referenz?: string | null;
  notiz?: string | null;
  created_by: string;
  created_by_name?: string | null;
  created_at: string;
}

/** Zahlungseintrag in der Dokumentliste: wer die Zahlung wann erfasst hat. */
export interface DokumentZahlungEintrag {
  betrag: number;
  bezahlt_am?: string | null;
  created_by?: string | null;
  created_by_name?: string | null;
  created_at?: string | null;
}

export type ZahlungProtokollAktion = "erfasst" | "geaendert" | "geloescht";

/** Protokollzeile: Erfassung, Änderung oder Löschung einer Zahlung. */
export interface DokumentZahlungProtokoll {
  id: number;
  dokument_id: number;
  zahlung_id?: number | null;
  aktion: ZahlungProtokollAktion;
  betrag?: number | null;
  bezahlt_am?: string | null;
  referenz?: string | null;
  notiz?: string | null;
  vorher_betrag?: number | null;
  vorher_bezahlt_am?: string | null;
  vorher_referenz?: string | null;
  vorher_notiz?: string | null;
  created_by: string;
  created_by_name?: string | null;
  created_at: string;
}

export interface ZahlungsuebersichtRow {
  dokument_id?: number | null;
  label: string;
  datum?: string | null;
  summe_netto: number;
  summe_steuer: number;
  summe_brutto: number;
  summe_bezahlt?: number;
  summe_offen?: number;
  zahlungsstatus?: Zahlungsstatus | string | null;
  zahlungsstatus_label?: string | null;
  is_current?: boolean;
}

export interface Zahlungsuebersicht {
  rows: ZahlungsuebersichtRow[];
  summe_brutto_gesamt?: number;
  offener_betrag_dieser_rechnung: number;
  offener_betrag_gesamt?: number;
  cta_betrag: number;
  cta_text: string;
}

export interface DokumentListItem {
  id: number;
  quelle?: DokumentQuelle;
  timesheet_id?: number;
  timesheet_status?: TimesheetListStatus | string | null;
  stueckliste_id?: number;
  /** Hero-Dokument-ID (virtuelle Hero-Zeile oder aus Hero aktiviertes GD). */
  hero_document_id?: number | null;
  dokument_nummer: string | null;
  /** Anzeigenummer inkl. Revisions-Suffix (.1 / .2). */
  display_number?: string | null;
  base_dokument_id?: number | null;
  revision_index?: number | null;
  supersedes_dokument_id?: number | null;
  has_newer_revision?: boolean;
  dokument_typ: DokumentTyp;
  rechnung_art?: RechnungArt | null;
  status: DokumentStatus;
  /** Aggregierte Zahlungen (nur Rechnungen). */
  summe_bezahlt?: number;
  summe_offen?: number;
  zahlungsstatus?: Zahlungsstatus | null;
  /** Erfasste Zahlungen, neueste Erfassung zuerst (nur Rechnungen). */
  zahlung_eintraege?: DokumentZahlungEintrag[];
  /** Zahlungsprotokoll, neueste Aktion zuerst (nur Rechnungen). */
  zahlung_protokoll?: DokumentZahlungProtokoll[];
  /** Mahnwesen (nur Rechnungen). */
  faelligkeitsdatum?: string | null;
  mahnstufe?: string | null;
  letzte_mahnung_am?: string | null;
  mahnung_pausiert?: boolean;
  mahnung_faellig?: boolean;
  naechste_mahnung_stufe?: string | null;
  naechste_mahnung_empfohlen_am?: string | null;
  tage_ueberfaellig?: number;
  auftrag_id: number | null;
  auftrag_nummer?: string | null;
  auftrag_beschreibung?: string | null;
  auftrag_bestellnummer?: string | null;
  auftrag_bestellnummer_quelle?: "regel" | "manuell" | null;
  auftrag_bestellnummer_regel_wert?: string | null;
  auftrag_kundenreferenz?: string | null;
  auftrag_leitweg_id?: string | null;
  leitweg_id?: string | null;
  /** Gültiger Vorschlag aus Auftrag oder Kundenstamm, nur bei leerem Entwurf. */
  leitweg_id_vorschlag?: string | null;
  auftrag_ort?: string | null;
  auftrag_ort_zusatz?: string | null;
  auftrag_ansprechpartner?: string | null;
  auftrag_ordnernummer?: string | null;
  auftrag_jahr?: number | null;
  auftrag_workflow_status?: string | null;
  hero_project_id?: number | null;
  kunden_id: number | null;
  kunde_name?: string | null;
  kunde_stamm_name?: string | null;
  belegdatum?: string | null;
  gueltig_bis?: string | null;
  versendet_am?: string | null;
  summe_netto: number;
  summe_steuer: number;
  summe_brutto: number;
  waehrung: string;
  betreff?: string | null;
  created_by: string;
  created_by_name?: string | null;
  created_by_email?: string | null;
  created_by_telefon?: string | null;
  created_by_mobil?: string | null;
  created_at: string;
  updated_at: string;
  kalkulation_pdf_at?: string | null;
  sharepoint_web_url?: string | null;
  customer_signed_at?: string | null;
  /** Nur Lieferscheine: unterschriebenes PDF liegt vor. */
  has_signed_pdf?: boolean;
  beauftragt_am?: string | null;
  beauftragung_sharepoint_item_id?: string | null;
  beauftragung_sharepoint_web_url?: string | null;
  beauftragung_filename?: string | null;
  beauftragung_at?: string | null;
  ist_beauftragt?: boolean;
  kalkulation_stunden?: KalkulationStundenZeile[];
  rechnungsformat?: Rechnungsformat | null;
  hat_zugferd_pdf?: boolean;
  hat_xrechnung_xml?: boolean;
  versand_rechnungsformat?: Rechnungsformat | null;
  bestellnummer?: string | null;
}

export interface KalkulationStundenZeile {
  lohnart_id: string;
  bezeichnung: string;
  stunden: number;
}

export interface BeauftragbaresAngebot {
  id: number;
  dokument_nummer: string | null;
  display_number?: string | null;
  revision_index?: number | null;
  status: DokumentStatus;
  summe_brutto: number;
  waehrung: string;
  belegdatum?: string | null;
  betreff?: string | null;
  created_at?: string | null;
  auftrag_id?: number | null;
  auftrag_nummer?: string | null;
  auftrag_beschreibung?: string | null;
  auftrag_ort?: string | null;
  kunde_name?: string | null;
}

export interface DokumentRechnungsadresse {
  id: number;
  bezeichnung?: string | null;
  firma?: string | null;
  adresszusatz?: string | null;
  strasse?: string | null;
  hausnummer?: string | null;
  plz?: string | null;
  ort?: string | null;
  land?: string | null;
  is_default: boolean;
}

export interface DokumentDetail extends DokumentListItem {
  ansprechpartner_id?: number | null;
  kunde_adresszusatz?: string | null;
  kunde_strasse?: string | null;
  kunde_hausnummer?: string | null;
  kunde_plz?: string | null;
  kunde_ort?: string | null;
  kunde_email?: string | null;
  kunde_telefon?: string | null;
  kundennummer?: string | null;
  ansprechpartner_name?: string | null;
  ansprechpartner_email?: string | null;
  ansprechpartner_telefon?: string | null;
  ansprechpartner_anrede?: string | null;
  ansprechpartner_vorname?: string | null;
  ansprechpartner_nachname?: string | null;
  auftrag_strasse?: string | null;
  auftrag_plz?: string | null;
  auftrag_ort_name?: string | null;
  gueltig_bis?: string | null;
  leistungsdatum?: string | null;
  lieferdatum?: string | null;
  leistungszeitraum_von?: string | null;
  leistungszeitraum_bis?: string | null;
  einleitung?: string | null;
  schlusstext?: string | null;
  interne_notiz?: string | null;
  rechnungsadresse_id?: number | null;
  rechnungsadressen?: DokumentRechnungsadresse[];
  /** § 13b UStG (Reverse Charge) – Positionen werden serverseitig auf 0 % USt gesetzt. */
  reverse_charge_13b?: boolean;
  /** USt-IdNr. des Kunden (E-Rechnung BT-48, Pflicht bei § 13b). */
  kunde_ust_id?: string | null;
  /** False = Referenzdokument (Dokumentenverknüpfung) nicht im PDF/Infoblock zeigen. */
  referenzdokument_anzeigen?: boolean;
  version: number;
  positionen: DokumentPosition[];
  verknuepfungen: DokumentVerknuepfung[];
  zahlungen?: DokumentZahlung[];
  abschlag_modus?: AbschlagModus | null;
  abschlag_prozent?: number | null;
  abschlag_betrag_netto?: number | null;
  abschlag_quelle_dokument_id?: number | null;
  zahlungsuebersicht?: Zahlungsuebersicht | null;
}

/**
 * Empfängerfelder aus einer Rechnungsadresse – nur Anschrift.
 * Der Ansprechpartner kommt vom Projekt/Kunden und wird nie überschrieben.
 */
export function applyRechnungsadresseToEmpfaenger(
  addr: DokumentRechnungsadresse,
): Pick<
  DokumentDetail,
  | "rechnungsadresse_id"
  | "kunde_name"
  | "kunde_adresszusatz"
  | "kunde_strasse"
  | "kunde_hausnummer"
  | "kunde_plz"
  | "kunde_ort"
> {
  return {
    rechnungsadresse_id: addr.id,
    kunde_adresszusatz: addr.adresszusatz ?? "",
    kunde_strasse: addr.strasse ?? "",
    kunde_hausnummer: addr.hausnummer ?? "",
    kunde_plz: addr.plz ?? "",
    kunde_ort: addr.ort ?? "",
    ...(addr.firma ? { kunde_name: addr.firma } : {}),
  };
}

export interface GeschaeftspartnerQuelle {
  geschaeftspartner_id: number;
  kunden_id?: number | null;
  kunde_name?: string | null;
  kundennummer?: string | null;
  kunde_email?: string | null;
  kunde_telefon?: string | null;
  kunde_strasse?: string | null;
  kunde_hausnummer?: string | null;
  kunde_plz?: string | null;
  kunde_ort?: string | null;
  kunde_adresszusatz?: string | null;
  reverse_charge_13b?: boolean;
  kunde_ust_id?: string | null;
  rechnungsadressen?: DokumentRechnungsadresse[];
}

/** Empfängerfelder aus einem App-Kunden (Kontakte). */
export function applyGeschaeftspartnerQuelle(
  quelle: GeschaeftspartnerQuelle,
  options?: { dokumentTyp?: DokumentTyp },
): Partial<DokumentDetail> {
  const isRechnung = options?.dokumentTyp === "rechnung";
  const addrs = quelle.rechnungsadressen || [];
  const defaultAddr = addrs.find((a) => a.is_default) || addrs[0] || null;
  const patch: Partial<DokumentDetail> = {
    kunden_id: quelle.kunden_id ?? null,
    kunde_name: quelle.kunde_name ?? "",
    kundennummer: quelle.kundennummer ?? null,
    kunde_email: quelle.kunde_email ?? "",
    kunde_telefon: quelle.kunde_telefon ?? "",
    kunde_strasse: quelle.kunde_strasse ?? "",
    kunde_hausnummer: quelle.kunde_hausnummer ?? "",
    kunde_plz: quelle.kunde_plz ?? "",
    kunde_ort: quelle.kunde_ort ?? "",
    kunde_adresszusatz: quelle.kunde_adresszusatz ?? "",
    rechnungsadressen: addrs,
    rechnungsadresse_id: null,
    ansprechpartner_id: null,
    ansprechpartner_name: "",
    ansprechpartner_email: "",
    ansprechpartner_telefon: "",
  };
  if (isRechnung && defaultAddr) {
    Object.assign(patch, applyRechnungsadresseToEmpfaenger(defaultAddr));
  }
  if (isRechnung) {
    patch.reverse_charge_13b = Boolean(quelle.reverse_charge_13b);
    patch.kunde_ust_id = quelle.kunde_ust_id ?? null;
  }
  return patch;
}

export function geschaeftspartnerQuelleToUpdate(
  quelle: GeschaeftspartnerQuelle,
  dokumentTyp: DokumentTyp,
): DokumentUpdateInput {
  const patch = applyGeschaeftspartnerQuelle(quelle, { dokumentTyp });
  return {
    kunden_id: patch.kunden_id,
    kunde_name: patch.kunde_name,
    kunde_adresszusatz: patch.kunde_adresszusatz,
    kunde_strasse: patch.kunde_strasse,
    kunde_hausnummer: patch.kunde_hausnummer,
    kunde_plz: patch.kunde_plz,
    kunde_ort: patch.kunde_ort,
    kunde_email: patch.kunde_email,
    kunde_telefon: patch.kunde_telefon,
    kundennummer: patch.kundennummer,
    ansprechpartner_id: patch.ansprechpartner_id,
    ansprechpartner_name: patch.ansprechpartner_name,
    ansprechpartner_email: patch.ansprechpartner_email,
    ansprechpartner_telefon: patch.ansprechpartner_telefon,
    rechnungsadresse_id: patch.rechnungsadresse_id,
    ...(dokumentTyp === "rechnung"
      ? {
          reverse_charge_13b: patch.reverse_charge_13b,
          kunde_ust_id: patch.kunde_ust_id,
        }
      : {}),
  };
}

export interface DokumentStatusUpdateResult extends DokumentDetail {
  /** Gesetzt, wenn beim Stornieren einer finalisierten Rechnung eine Stornorechnung erzeugt wurde. */
  storno_dokument_id?: number | null;
  storno_dokument?: DokumentDetail | null;
}

export interface ZahlungCreateInput {
  betrag: number;
  bezahlt_am?: string | null;
  referenz?: string | null;
  notiz?: string | null;
}

export interface DokumentCreateInput {
  dokument_typ: DokumentTyp;
  auftrag_id: number;
  kunden_id?: number | null;
  ansprechpartner_id?: number | null;
  quell_dokument_id?: number | null;
  beauftrag_angebot_id?: number | null;
  betreff?: string | null;
  einleitung?: string | null;
  schlusstext?: string | null;
  rechnung_art?: RechnungArt | null;
  positionen?: DokumentPosition[];
  abschlag_modus?: AbschlagModus | null;
  abschlag_prozent?: number | null;
  abschlag_betrag_netto?: number | null;
  schlussrechnung_mit_abzuegen?: boolean;
}

export interface DokumentUpdateInput {
  version?: number;
  auftrag_id?: number | null;
  kunden_id?: number | null;
  ansprechpartner_id?: number | null;
  belegdatum?: string | null;
  gueltig_bis?: string | null;
  leistungsdatum?: string | null;
  lieferdatum?: string | null;
  leistungszeitraum_von?: string | null;
  leistungszeitraum_bis?: string | null;
  betreff?: string | null;
  einleitung?: string | null;
  schlusstext?: string | null;
  interne_notiz?: string | null;
  kunde_name?: string | null;
  kunde_adresszusatz?: string | null;
  kunde_strasse?: string | null;
  kunde_hausnummer?: string | null;
  kunde_plz?: string | null;
  kunde_ort?: string | null;
  kunde_email?: string | null;
  kunde_telefon?: string | null;
  kundennummer?: string | null;
  ansprechpartner_name?: string | null;
  ansprechpartner_email?: string | null;
  ansprechpartner_telefon?: string | null;
  created_by_name?: string | null;
  created_by_email?: string | null;
  created_by_telefon?: string | null;
  created_by_mobil?: string | null;
  bestellnummer?: string | null;
  leitweg_id?: string | null;
  /** null = normale Rechnung (ohne Teil-/Schluss-/Vorkasse). */
  rechnung_art?: RechnungArt | null;
  rechnungsadresse_id?: number | null;
  reverse_charge_13b?: boolean;
  kunde_ust_id?: string | null;
  referenzdokument_anzeigen?: boolean;
}

export interface AuftragQuelle {
  auftrag_id: number;
  auftragsnummer?: string | null;
  kunden_id?: number | null;
  ansprechpartner_id?: number | null;
  betreff?: string | null;
  kunde_name?: string | null;
  kunde_email?: string | null;
  kunde_telefon?: string | null;
  kunde_strasse?: string | null;
  kunde_plz?: string | null;
  kunde_ort?: string | null;
  kundennummer?: string | null;
  bestellnummer?: string | null;
  ansprechpartner_name?: string | null;
  ansprechpartner_email?: string | null;
  ansprechpartner_telefon?: string | null;
  positionen: DokumentPosition[];
}

export const DOKUMENT_TYP_LABELS: Record<DokumentTyp, string> = {
  angebot: "Angebot",
  rechnung: "Rechnung",
  lieferschein: "Lieferschein",
  auftragsbestaetigung: "Auftragsbestätigung",
  stundennachweis: "Stundennachweis",
  stueckliste: "Stückliste",
};

export const RECHNUNG_ART_LABELS: Record<RechnungArt, string> = {
  teilrechnung: "Teilrechnung",
  schlussrechnung: "Schlussrechnung",
  stornorechnung: "Stornorechnung",
  vorkasse: "Vorkasse-Rechnung",
};

/** Manuell wählbare Rechnungsarten (Stornorechnung entsteht nur per Storno). */
export const SELECTABLE_RECHNUNG_ARTEN: RechnungArt[] = [
  "teilrechnung",
  "schlussrechnung",
  "vorkasse",
];

/** Select-Optionen inkl. normaler Rechnung (API: rechnung_art = null). */
export const SELECTABLE_RECHNUNG_ART_OPTIONS: Array<{
  value: "" | RechnungArt;
  label: string;
}> = [
  { value: "", label: "Rechnung" },
  ...SELECTABLE_RECHNUNG_ARTEN.map((art) => ({
    value: art,
    label: RECHNUNG_ART_LABELS[art],
  })),
];

export const BILLING_REOPEN_TARGET_LABELS: Record<BillingReopenTarget, string> = {
  teilrechnung: "Teilrechnung",
  vorkasse: "Vorkasse-Rechnung",
};

/** Erstellte Schluss-/Standard-Rechnung darf Büro als Teil-/Vorkasse ummarkieren. */
export function canBillingReopenDokument(
  doc: Pick<DokumentListItem, "dokument_typ" | "status" | "rechnung_art" | "auftrag_id">,
): boolean {
  if (doc.dokument_typ !== "rechnung" || !doc.auftrag_id) return false;
  if (doc.status === "entwurf" || doc.status === "storniert") return false;
  const art = doc.rechnung_art;
  if (art === "stornorechnung") return false;
  // Bereits Teil-/Vorkasse: Umschalten zwischen beiden weiterhin möglich.
  return true;
}

export function getDokumentTypLabel(
  doc: Pick<DokumentListItem, "dokument_typ" | "rechnung_art">,
): string {
  if (doc.dokument_typ === "rechnung" && doc.rechnung_art) {
    return RECHNUNG_ART_LABELS[doc.rechnung_art] || DOKUMENT_TYP_LABELS.rechnung;
  }
  return DOKUMENT_TYP_LABELS[doc.dokument_typ];
}

export const TIMESHEET_STATUS_LABELS: Record<TimesheetListStatus, string> = {
  draft: "Entwurf",
  ready: "Bereit für Unterschrift",
  signed: "Unterschrieben",
  invoice_draft: "Rechnungsentwurf erstellt",
  invoice_created: "Abgerechnet",
  invoice_paid: "Bezahlt",
  archived: "Archiviert",
};

export const TIMESHEET_STATUS_BADGE: Record<TimesheetListStatus, string> = {
  draft: "bg-fill-tertiary text-label-secondary",
  ready: "bg-accent-soft text-accent",
  signed: "bg-status-blue/15 text-status-blue",
  invoice_draft: "bg-status-orange/15 text-status-orange",
  invoice_created: "bg-status-green/15 text-status-green",
  invoice_paid: "bg-status-green/15 text-status-green",
  archived: "bg-fill-tertiary text-label-tertiary line-through",
};

export function isStundennachweisListItem(
  doc: Pick<DokumentListItem, "quelle" | "dokument_typ">,
): boolean {
  return doc.quelle === "stundennachweis" || doc.dokument_typ === "stundennachweis";
}

export function isStuecklisteListItem(
  doc: Pick<DokumentListItem, "quelle" | "dokument_typ">,
): boolean {
  return doc.quelle === "stueckliste" || doc.dokument_typ === "stueckliste";
}

export function isHeroCustomerDocumentListItem(
  doc: Pick<DokumentListItem, "quelle">,
): boolean {
  return doc.quelle === "hero_customer_document";
}

/** Virtuelles Hero-Dokument oder in der App aus Hero aktiviertes Geschäftsdokument. */
export function isHeroOriginDokument(
  doc: Pick<DokumentListItem, "quelle" | "hero_document_id">,
): boolean {
  return isHeroCustomerDocumentListItem(doc) || Boolean(doc.hero_document_id);
}

export const ZAHLUNGSSTATUS_LABELS: Record<Zahlungsstatus, string> = {
  offen: "Offen",
  teilbezahlt: "Teilbezahlt",
  bezahlt: "Bezahlt",
};

export function getDokumentListStatusLabel(
  doc: Pick<
    DokumentListItem,
    "status" | "timesheet_status" | "quelle" | "dokument_typ" | "zahlungsstatus"
  >,
): string {
  if (isStundennachweisListItem(doc)) {
    const key = String(doc.timesheet_status || "draft").toLowerCase() as TimesheetListStatus;
    return TIMESHEET_STATUS_LABELS[key] || String(doc.timesheet_status || "—");
  }
  if (doc.dokument_typ === "rechnung") {
    if (doc.zahlungsstatus === "teilbezahlt") return ZAHLUNGSSTATUS_LABELS.teilbezahlt;
    if (doc.zahlungsstatus === "bezahlt" || doc.status === "abgerechnet") {
      return ZAHLUNGSSTATUS_LABELS.bezahlt;
    }
  }
  return DOKUMENT_STATUS_LABELS[doc.status];
}

export function getDokumentListStatusBadge(
  doc: Pick<
    DokumentListItem,
    "status" | "timesheet_status" | "quelle" | "dokument_typ" | "zahlungsstatus"
  >,
): string {
  if (isStundennachweisListItem(doc)) {
    const key = String(doc.timesheet_status || "draft").toLowerCase() as TimesheetListStatus;
    return TIMESHEET_STATUS_BADGE[key] || TIMESHEET_STATUS_BADGE.draft;
  }
  if (doc.dokument_typ === "rechnung") {
    if (doc.zahlungsstatus === "teilbezahlt") {
      return "bg-status-orange/15 text-status-orange";
    }
    if (doc.zahlungsstatus === "bezahlt" || doc.status === "abgerechnet") {
      return DOKUMENT_STATUS_BADGE.abgerechnet;
    }
  }
  return DOKUMENT_STATUS_BADGE[doc.status];
}

export const DOKUMENT_STATUS_LABELS: Record<DokumentStatus, string> = {
  entwurf: "Entwurf",
  erstellt: "Erstellt",
  versendet: "Versendet",
  storniert: "Storniert",
  angenommen: "Angenommen",
  abgelehnt: "Abgelehnt",
  abgerechnet: "Bezahlt",
  geliefert: "Geliefert",
};

export const DOKUMENT_STATUS_BADGE: Record<DokumentStatus, string> = {
  entwurf: "bg-fill-tertiary text-label-secondary",
  erstellt: "bg-accent-soft text-accent",
  versendet: "bg-status-blue/15 text-status-blue",
  storniert: "bg-fill-tertiary text-label-tertiary line-through",
  angenommen: "bg-status-green/15 text-status-green",
  abgelehnt: "bg-status-orange/15 text-status-orange",
  abgerechnet: "bg-status-green/15 text-status-green",
  geliefert: "bg-status-green/15 text-status-green",
};

export const CONVERSION_TARGETS: Record<DokumentTyp, DokumentTyp[]> = {
  // Angebot → AB läuft über Angenommen / Beauftragung (Bestellnummer).
  // LS/Rechnung aus Angebot (inkl. aktivierter Hero-Angebote) über Umwandlung.
  angebot: ["lieferschein", "rechnung"],
  auftragsbestaetigung: ["lieferschein", "rechnung"],
  lieferschein: ["rechnung"],
  rechnung: [],
  stundennachweis: [],
  stueckliste: [],
};

function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      sp.set(key, String(value));
    }
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

export type ListDokumenteParams = {
  typ?: DokumentTyp | "";
  status?: DokumentStatus | "";
  kunden_id?: number;
  auftrag_id?: number;
  /** Kommagetrennte oder als Array übergebene Auftrag-IDs (z. B. aus Projektsuche). */
  auftrag_ids?: number[] | string;
  datum_von?: string;
  datum_bis?: string;
  q?: string;
  limit?: number;
  offset?: number;
  sort?: string;
  direction?: "asc" | "desc";
};

export type ListDokumenteResult = {
  items: DokumentListItem[];
  /** Gesamtanzahl für die aktuellen Filter (Header `X-Total-Count`). */
  totalCount: number | null;
};

function parseTotalCountHeader(headers: Headers): number | null {
  const raw = headers.get("X-Total-Count");
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function buildListDokumenteQuery(filters: ListDokumenteParams = {}): string {
  const auftragIds =
    Array.isArray(filters.auftrag_ids)
      ? filters.auftrag_ids.filter((id) => Number.isFinite(id) && id > 0).join(",")
      : typeof filters.auftrag_ids === "string"
        ? filters.auftrag_ids.trim()
        : undefined;
  return buildQuery({
    typ: filters.typ || undefined,
    status: filters.status || undefined,
    kunden_id: filters.kunden_id,
    auftrag_id: filters.auftrag_id,
    auftrag_ids: auftragIds || undefined,
    datum_von: filters.datum_von,
    datum_bis: filters.datum_bis,
    q: filters.q,
    limit: filters.limit,
    offset: filters.offset,
    sort: filters.sort,
    direction: filters.direction,
  });
}

export async function listDokumenteWithTotal(
  filters?: ListDokumenteParams,
  opts?: { signal?: AbortSignal },
): Promise<ListDokumenteResult> {
  const qs = buildListDokumenteQuery(filters);
  const { data, headers } = await api.getWithMeta<DokumentListItem[]>(
    `/dokumente${qs}`,
    opts?.signal ? { signal: opts.signal } : undefined,
  );
  return {
    items: Array.isArray(data) ? data : [],
    totalCount: parseTotalCountHeader(headers),
  };
}

export async function listDokumente(filters?: ListDokumenteParams): Promise<DokumentListItem[]> {
  return listDokumenteWithTotal(filters).then((result) => result.items);
}

export async function getDokument(id: number): Promise<DokumentDetail> {
  return api.get<DokumentDetail>(`/dokumente/${id}`);
}

export async function createDokument(payload: DokumentCreateInput): Promise<DokumentDetail> {
  return api.post<DokumentDetail>("/dokumente", payload);
}

/** Kopf-Updates inkl. großer Entwürfe – länger als Default (30s), wegen Serverlast/Backups. */
const DOKUMENT_UPDATE_TIMEOUT_MS = 90_000;
/** Positions-Replace kann bei vielen Zeilen und DB-Last deutlich länger brauchen. */
const DOKUMENT_POSITIONEN_TIMEOUT_MS = 180_000;

export async function updateDokument(
  id: number,
  payload: DokumentUpdateInput,
): Promise<DokumentDetail> {
  return api.patch<DokumentDetail>(`/dokumente/${id}`, payload, {
    timeoutMs: DOKUMENT_UPDATE_TIMEOUT_MS,
  });
}

export async function replaceDokumentReferenzen(
  id: number,
  payload: { referenz_dokument_ids: number[]; anzeigen: boolean; version?: number },
): Promise<DokumentDetail> {
  return api.put<DokumentDetail>(`/dokumente/${id}/referenzen`, payload);
}

export interface BillingReopenResult extends DokumentDetail {
  billing_reopen?: {
    target: BillingReopenTarget;
    reason: string;
    previous_rechnung_art?: RechnungArt | null;
    previous_project_completed?: boolean;
    project_completed?: boolean;
    previous_workflow_status?: string | null;
    workflow_status?: string | null;
    previous_zahlungsart?: string | null;
    zahlungsart?: string | null;
  };
}

export interface BillingReopenLogEntry {
  id: number;
  dokument_id?: number | null;
  hero_document_id?: number | null;
  auftrag_id?: number | null;
  action: string;
  target_rechnung_art: string;
  previous_rechnung_art?: string | null;
  reason: string;
  previous_project_completed?: boolean | null;
  new_project_completed?: boolean | null;
  previous_workflow_status?: string | null;
  new_workflow_status?: string | null;
  previous_zahlungsart?: string | null;
  new_zahlungsart?: string | null;
  created_by_login: string;
  created_by_display?: string | null;
  created_at: string;
}

export interface HeroBillingReopenResult {
  hero_document_id: number;
  auftrag_id: number;
  auftrag_nummer?: string | number | null;
  billing_art_override: BillingReopenTarget;
  effective_rechnung_art?: string | null;
  billing_reopen?: BillingReopenResult["billing_reopen"];
}

/** Büro: Rechnung als Teilrechnung/Vorkasse markieren und Fertigstellung aufheben. */
export async function markDokumentBillingReopen(
  id: number,
  payload: { target: BillingReopenTarget; reason: string },
): Promise<BillingReopenResult> {
  return api.post<BillingReopenResult>(`/dokumente/${id}/billing-reopen`, payload);
}

export async function listDokumentBillingReopenLog(
  id: number,
  limit = 20,
): Promise<BillingReopenLogEntry[]> {
  const data = await api.get<BillingReopenLogEntry[]>(
    `/dokumente/${id}/billing-reopen-log?limit=${limit}`,
  );
  return Array.isArray(data) ? data : [];
}

/** Büro: Hero-Rechnung lokal als Teilrechnung/Vorkasse markieren. */
export async function markHeroBillingReopen(
  heroDocumentId: number,
  payload: { target: BillingReopenTarget; reason: string; auftrag_id?: number },
): Promise<HeroBillingReopenResult> {
  return api.post<HeroBillingReopenResult>(
    `/dokumente/hero/${heroDocumentId}/billing-reopen`,
    payload,
  );
}

export async function listHeroBillingReopenLog(
  heroDocumentId: number,
  limit = 20,
): Promise<BillingReopenLogEntry[]> {
  const data = await api.get<BillingReopenLogEntry[]>(
    `/dokumente/hero/${heroDocumentId}/billing-reopen-log?limit=${limit}`,
  );
  return Array.isArray(data) ? data : [];
}

export async function updateDokumentStatus(
  id: number,
  status: DokumentStatus,
): Promise<DokumentStatusUpdateResult> {
  return api.patch<DokumentStatusUpdateResult>(`/dokumente/${id}/status`, { status });
}

/** Neue Angebots-Revision anlegen (alte Version bleibt einsehbar). */
export async function createDokumentRevision(id: number): Promise<DokumentDetail> {
  return api.post<DokumentDetail>(`/dokumente/${id}/create-revision`, {});
}

/** Neuen Rechnungsentwurf aus stornierter Rechnung anlegen. */
export async function createInvoiceDraftFromCancelled(id: number): Promise<DokumentDetail> {
  return api.post<DokumentDetail>(`/dokumente/${id}/create-draft-from-cancelled`, {});
}

/** Leere Stornorechnung: Positionen aus Quellrechnung/Hero nachziehen (Nummer bleibt). */
export async function repairStornoPositionen(id: number): Promise<DokumentDetail & { repaired_position_count?: number }> {
  return api.post<DokumentDetail & { repaired_position_count?: number }>(
    `/dokumente/${id}/repair-storno-positionen`,
    {},
  );
}

export async function createDokumentZahlung(
  docId: number,
  payload: ZahlungCreateInput,
): Promise<DokumentDetail> {
  return api.post<DokumentDetail>(`/dokumente/${docId}/zahlungen`, payload);
}

export async function updateDokumentZahlung(
  docId: number,
  zahlungId: number,
  payload: ZahlungCreateInput,
): Promise<DokumentDetail> {
  return api.patch<DokumentDetail>(`/dokumente/${docId}/zahlungen/${zahlungId}`, payload);
}

export async function deleteDokumentZahlung(
  docId: number,
  zahlungId: number,
): Promise<DokumentDetail> {
  return api.del<DokumentDetail>(`/dokumente/${docId}/zahlungen/${zahlungId}`);
}

export async function replaceDokumentPositionen(
  id: number,
  positionen: DokumentPosition[],
): Promise<DokumentDetail> {
  return api.put<DokumentDetail>(
    `/dokumente/${id}/positionen`,
    { positionen },
    { timeoutMs: DOKUMENT_POSITIONEN_TIMEOUT_MS },
  );
}

export async function convertDokument(
  id: number,
  zielTyp: DokumentTyp,
  options?: {
    positionen?: DokumentPosition[];
    bestellnummer?: string | null;
  },
): Promise<DokumentDetail> {
  return api.post<DokumentDetail>(`/dokumente/${id}/umwandeln`, {
    ziel_typ: zielTyp,
    positionen: options?.positionen,
    bestellnummer: options?.bestellnummer?.trim() || null,
  });
}

export async function fetchBestellnummerVorschlag(
  docId: number,
): Promise<{ bestellnummer: string | null }> {
  return api.get<{ bestellnummer: string | null }>(`/dokumente/${docId}/bestellnummer-vorschlag`);
}

export interface DocumentChainStep {
  key: string;
  kind: string;
  label: string;
  doc_id?: number | null;
  timesheet_id?: number | null;
  /** Zahlungserinnerung oder Mahnung zu einer Rechnung der Kette. */
  mahnung_id?: number | null;
  dokument_nummer?: string | null;
  status?: string | null;
  timesheet_signed?: boolean;
  /** Nur Lieferscheine: unterschriebenes PDF liegt vor (Ansicht zeigt dann dieses). */
  has_signed_pdf?: boolean;
  /** Geöffnetes Dokument im Editor. */
  is_current?: boolean;
  /** Neueste Angebots-Version der Revisionskette (nur am Angebot-Tab). */
  is_latest?: boolean;
  revision_index?: number | null;
}

/**
 * PDF-Variante für einen Ketten-Schritt: Kalkulation/Beauftragung, unterschriebener
 * Lieferschein („signed“) oder das normale Dokument-PDF.
 */
export function documentChainStepPdfVariant(
  step: Pick<DocumentChainStep, "kind" | "has_signed_pdf"> | null | undefined,
): DokumentPdfOpenVariant | "signed" {
  if (!step) return "angebot";
  if (step.kind === "kalkulation") return "kalkulation";
  if (step.kind === "beauftragung") return "beauftragung";
  if (step.kind === "lieferschein" && step.has_signed_pdf) return "signed";
  return "angebot";
}

/** Tab-Beschriftung inkl. Nummer (ohne „aktuell“ – das kommt als Badge). */
export function formatDocumentChainStepLabel(step: DocumentChainStep): string {
  const base = String(step.label || step.kind || "Dokument").trim();
  const nummer = String(step.dokument_nummer || "").trim();
  if (nummer && !base.includes(nummer)) {
    return `${base} ${nummer}`;
  }
  return base;
}

export interface DocumentChain {
  auftrag_id: number | null;
  angebot_id: number | null;
  current_key: string;
  bestellnummer: string | null;
  steps: DocumentChainStep[];
}

export async function fetchDocumentChain(docId: number): Promise<DocumentChain> {
  return api.get<DocumentChain>(`/dokumente/${docId}/kette`);
}

export async function deleteDokument(id: number): Promise<void> {
  await api.del(`/dokumente/${id}`);
}

export async function getAuftragQuelle(auftragId: number): Promise<AuftragQuelle> {
  return api.get<AuftragQuelle>(`/dokumente/quellen/auftrag/${auftragId}`);
}

export async function getGeschaeftspartnerQuelle(
  geschaeftspartnerId: number,
): Promise<GeschaeftspartnerQuelle> {
  return api.get<GeschaeftspartnerQuelle>(
    `/dokumente/quellen/geschaeftspartner/${geschaeftspartnerId}`,
  );
}

export interface BueroAnsprechpartner {
  user_login: string;
  display_name: string;
}

export interface BueroAnsprechpartnerKontakt extends BueroAnsprechpartner {
  created_by_name?: string | null;
  created_by_email?: string | null;
  created_by_telefon?: string | null;
  created_by_mobil?: string | null;
}

/** Büro-Mitarbeiter für Autocomplete „Ansprechpartner (Ersteller)“. */
export async function listBueroAnsprechpartner(
  q = "",
  limit = 50,
): Promise<BueroAnsprechpartner[]> {
  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  params.set("limit", String(limit));
  const data = await api.get<BueroAnsprechpartner[]>(
    `/dokumente/buero-ansprechpartner?${params.toString()}`,
  );
  return Array.isArray(data) ? data : [];
}

/** Kontaktdaten eines Büro-Mitarbeiters für den Ersteller-Infoblock. */
export async function getBueroAnsprechpartnerKontakt(
  login: string,
): Promise<BueroAnsprechpartnerKontakt> {
  const params = new URLSearchParams({ login: login.trim() });
  return api.get<BueroAnsprechpartnerKontakt>(
    `/dokumente/buero-ansprechpartner/kontakt?${params.toString()}`,
  );
}

export interface AuftragEinkaufArtikel {
  bezeichnung: string;
  seller_article_nr?: string | null;
  ean?: string | null;
  einheit?: string | null;
  menge?: number | null;
  einzelpreis_netto?: number | null;
  stamm_artikel_id?: number | null;
  stamm_hero_nr?: string | null;
  stamm_artikel_bezeichnung?: string | null;
  lieferant_firmenname?: string | null;
  positionen_anzahl?: number | null;
}

export async function getAuftragEinkaufArtikel(
  auftragId: number,
): Promise<{ auftrag_id: number; items: AuftragEinkaufArtikel[]; total: number }> {
  return api.get(`/dokumente/quellen/auftrag/${auftragId}/einkauf-artikel`);
}

export async function listBeauftragbareAngebote(
  options: { auftragId?: number; q?: string; limit?: number } = {},
): Promise<BeauftragbaresAngebot[]> {
  const qs = buildQuery({
    auftrag_id: options.auftragId,
    q: options.q,
    limit: options.limit,
  });
  return api.get<BeauftragbaresAngebot[]>(`/dokumente/beauftragbare-angebote${qs}`);
}

export async function getArtikelQuelle(artikelId: number): Promise<ArtikelQuelle> {
  return api.get<ArtikelQuelle>(`/dokumente/quellen/artikel/${artikelId}`);
}

export interface KundenStundenArtikelQuelle
  extends Omit<Partial<ArtikelQuelle>, "artikel_id" | "bezeichnung" | "einzelpreis_netto"> {
  hero_nr?: string | null;
  artikel_id?: number | null;
  bezeichnung?: string | null;
  einheit?: string | null;
  einzelpreis_netto?: number | null;
  warning?: string | null;
  /** Aufgelöste Hero-Kunden-/Kontakt-ID für das Mapping. */
  hero_customer_id?: number | null;
  hero_customer_ids?: number[];
  kunden_id?: number | null;
  auftrag_id?: number | null;
}

function toFiniteId(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

type KundenStundenArtikelOptions = {
  auftragId?: number | null;
  /** Hero-Kunden-ID (optional; Backend löst sie auch über Auftrag/Kunde auf). */
  heroCustomerId?: number | null;
  /** Hero-Kontakt-ID am Projekt. */
  heroContactId?: number | null;
};

function stundenArtikelRequestKey(
  kundenId?: number | null,
  options: KundenStundenArtikelOptions = {},
): string {
  return [
    kundenId ?? "",
    options.auftragId ?? "",
    options.heroCustomerId ?? "",
    options.heroContactId ?? "",
  ].join("|");
}

/** In-flight/Session-Cache: Prefetch beim Dokumentöffnen, sofortige Nutzung beim Platzieren. */
const stundenArtikelRequestCache = new Map<string, Promise<KundenStundenArtikelQuelle>>();

/** Stunden-Artikel aus „Monteur-Stundensätze je Kunde“ (kundenspezifisch oder Standard). */
export async function getKundenStundenArtikel(
  kundenId?: number | null,
  options: KundenStundenArtikelOptions = {},
): Promise<KundenStundenArtikelQuelle> {
  const key = stundenArtikelRequestKey(kundenId, options);
  const cached = stundenArtikelRequestCache.get(key);
  if (cached) return cached;

  const qs = buildQuery({
    kunden_id: kundenId ?? undefined,
    auftrag_id: options.auftragId ?? undefined,
    hero_customer_id: toFiniteId(options.heroCustomerId),
    hero_contact_id: toFiniteId(options.heroContactId),
  });
  const request = api
    .get<KundenStundenArtikelQuelle>(`/dokumente/quellen/stunden-artikel${qs}`)
    .catch((err) => {
      stundenArtikelRequestCache.delete(key);
      throw err;
    });
  stundenArtikelRequestCache.set(key, request);
  return request;
}

/** Im Hintergrund vorladen, damit Nachweisarbeiten ohne Wartezeit platziert werden können. */
export function prefetchKundenStundenArtikel(
  kundenId?: number | null,
  options: KundenStundenArtikelOptions = {},
): void {
  if (
    kundenId == null &&
    options.auftragId == null &&
    options.heroCustomerId == null &&
    options.heroContactId == null
  ) {
    return;
  }
  void getKundenStundenArtikel(kundenId, options);
}

export async function getGdLohnarten(): Promise<GdLohnart[]> {
  const res = await api.get<{ lohnarten: GdLohnart[] }>("/dokumente/einstellungen/lohnarten");
  return res.lohnarten || [];
}

export async function saveGdLohnarten(lohnarten: GdLohnart[]): Promise<GdLohnart[]> {
  const res = await api.put<{ lohnarten: GdLohnart[] }>("/dokumente/einstellungen/lohnarten", {
    lohnarten,
  });
  return res.lohnarten || [];
}

export type DatevSettings = {
  email: string;
  from_address?: string;
};

export async function getDatevSettings(): Promise<DatevSettings> {
  const res = await api.get<DatevSettings>("/dokumente/einstellungen/datev");
  return { email: res.email || "", from_address: res.from_address || "" };
}

export async function saveDatevSettings(email: string): Promise<DatevSettings> {
  const res = await api.put<DatevSettings>("/dokumente/einstellungen/datev", { email });
  return { email: res.email || "", from_address: res.from_address || "" };
}

export type DatevBackfillResult = {
  sent: number;
  skipped: number;
  failed: number;
  total: number;
  from_date: string;
  errors?: Array<{ doc_id: number; error: string }>;
};

export async function runDatevBackfill(options: {
  fromDate: string;
  limit?: number;
}): Promise<DatevBackfillResult> {
  return api.post<DatevBackfillResult>("/dokumente/einstellungen/datev-backfill", {
    from_date: options.fromDate,
    ...(options.limit != null ? { limit: options.limit } : {}),
  });
}

export type SharepointBackfillResult = {
  uploaded: number;
  skipped: number;
  failed: number;
  total: number;
  errors?: Array<{ doc_id: number; error: string }>;
};

export async function runSharepointBackfill(options?: {
  force?: boolean;
  limit?: number;
}): Promise<SharepointBackfillResult> {
  return api.post<SharepointBackfillResult>("/dokumente/einstellungen/sharepoint-backfill", {
    force: options?.force ?? false,
    ...(options?.limit != null ? { limit: options.limit } : {}),
  });
}

export function formatBelegdatum(value?: string | null): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("de-DE");
}

/** Kalendertag Europe/Berlin als ISO-Datum (YYYY-MM-DD). */
export function todayIsoDateBerlin(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin" }).format(new Date());
}

/**
 * Belegdatum für Anzeige/Vorschau: Entwürfe immer heute,
 * damit Zahlungsziele nicht vom Entwurfs-Erstelldatum abhängen.
 */
export function effectiveBelegdatum(
  doc: Pick<{ status?: string | null; belegdatum?: string | null }, "status" | "belegdatum">,
): string | null {
  if (doc.status === "entwurf") return todayIsoDateBerlin();
  return doc.belegdatum ?? null;
}

/**
 * Empfängeradresse inkl. Adresszusatz und optional z. Hd. Ansprechpartner.
 * Rechnungen: kein „z. Hd.“ – der Ansprechpartner steht als
 * „Ihr Ansprechpartner:“ im Infoblock.
 */
export function buildRecipientAddressLines(
  doc: Pick<
    DokumentDetail,
    | "dokument_typ"
    | "kunde_name"
    | "kunde_adresszusatz"
    | "kunde_strasse"
    | "kunde_hausnummer"
    | "kunde_plz"
    | "kunde_ort"
    | "ansprechpartner_name"
  >,
): string[] {
  const lines: string[] = [];
  if (doc.kunde_name?.trim()) lines.push(doc.kunde_name.trim());
  for (const part of (doc.kunde_adresszusatz || "").split(/\r?\n/)) {
    if (part.trim()) lines.push(part.trim());
  }
  if (doc.ansprechpartner_name?.trim() && doc.dokument_typ !== "rechnung") {
    lines.push(`z. Hd. ${doc.ansprechpartner_name.trim()}`);
  }
  const street = [doc.kunde_strasse, doc.kunde_hausnummer].filter(Boolean).join(" ").trim();
  if (street) lines.push(street);
  const city = [doc.kunde_plz, doc.kunde_ort].filter(Boolean).join(" ").trim();
  if (city) lines.push(city);
  return lines;
}

function normAddrPart(value: string | null | undefined): string {
  return (value || "")
    .trim()
    .toLowerCase()
    .replace(/ß/g, "ss")
    .replace(/strasse|straße|str\./g, "str")
    .replace(/[^a-z0-9]/g, "");
}

function objectAddressFromDoc(
  doc: Pick<DokumentDetail, "auftrag_ort" | "auftrag_ort_name" | "auftrag_strasse" | "auftrag_plz">,
): { site: string; street: string; postcode: string; city: string } {
  const street = (doc.auftrag_strasse || "").trim();
  const postcode = (doc.auftrag_plz || "").trim();
  let city = (doc.auftrag_ort_name || "").trim();
  let site = (doc.auftrag_ort || "").trim();
  if (!city && site) {
    city = site;
    site = "";
  }
  if (site && normAddrPart(site) === normAddrPart(city)) {
    site = "";
  }
  return { site, street, postcode, city };
}

/**
 * Abweichende Objektadresse als eine Zeile, nur wenn sie nicht der
 * Rechnungsanschrift entspricht. Der Ansprechpartner allein reicht nicht.
 */
export function buildLeistungempfaengerLine(
  doc: Pick<
    DokumentDetail,
    | "dokument_typ"
    | "kunde_strasse"
    | "kunde_hausnummer"
    | "kunde_plz"
    | "kunde_ort"
    | "auftrag_ort"
    | "auftrag_ort_name"
    | "auftrag_strasse"
    | "auftrag_plz"
  >,
): string | null {
  if (doc.dokument_typ !== "rechnung") return null;
  const addr = objectAddressFromDoc(doc);
  const usable = Boolean(addr.street || (addr.postcode && addr.city));
  if (!usable) return null;

  const invoiceStreet = [doc.kunde_strasse, doc.kunde_hausnummer].filter(Boolean).join(" ").trim();
  const invoicePostcode = (doc.kunde_plz || "").trim();
  const invoiceCity = (doc.kunde_ort || "").trim();
  const hasInvoice = Boolean(invoiceStreet || invoicePostcode || invoiceCity);
  const differs =
    !hasInvoice ||
    normAddrPart(addr.street) !== normAddrPart(invoiceStreet) ||
    normAddrPart(addr.postcode) !== normAddrPart(invoicePostcode) ||
    normAddrPart(addr.city) !== normAddrPart(invoiceCity);
  if (!differs) return null;

  const cityLine = [addr.postcode, addr.city].filter(Boolean).join(" ").trim();
  const parts: string[] = [];
  const siteNorm = normAddrPart(addr.site);
  if (
    addr.site &&
    siteNorm &&
    !normAddrPart(addr.street).includes(siteNorm) &&
    !normAddrPart(cityLine).includes(siteNorm)
  ) {
    parts.push(addr.site);
  }
  if (addr.street) parts.push(addr.street);
  if (cityLine) parts.push(cityLine);
  return parts.length ? `Leistungsempfänger: ${parts.join(", ")}` : null;
}

export const REFERENZ_DOKUMENT_TYPEN: DokumentTyp[] = [
  "angebot",
  "lieferschein",
  "auftragsbestaetigung",
  "rechnung",
];

const REFERENZ_TYP_LABELS: Record<string, string> = {
  umwandlung: "Umwandlung",
  referenz: "Referenz",
  storno: "Storno",
  beauftragung: "Beauftragung",
  zuordnung: "Zuordnung",
};

export function verknuepfungTypLabel(typ: string | null | undefined): string {
  const key = String(typ || "").trim();
  return REFERENZ_TYP_LABELS[key] || key || "Verknüpfung";
}

function incomingLinksForDoc(
  doc: Pick<DokumentDetail, "id" | "verknuepfungen">,
): DokumentVerknuepfung[] {
  return (doc.verknuepfungen || []).filter((link) => link.ziel_dokument_id === doc.id);
}

function mergeReferenzLinks(
  base: DokumentVerknuepfung[],
  extra: DokumentVerknuepfung[],
): DokumentVerknuepfung[] {
  const seen = new Set<number>();
  const out: DokumentVerknuepfung[] = [];
  for (const link of [...base, ...extra]) {
    if (seen.has(link.quell_dokument_id)) continue;
    seen.add(link.quell_dokument_id);
    out.push(link);
  }
  return out;
}

/** Explizite Referenz-Links, sonst Umwandlungsquelle, plus nachträgliche Zuordnungen. */
export function resolveReferenzLinks(
  doc: Pick<DokumentDetail, "id" | "verknuepfungen">,
): DokumentVerknuepfung[] {
  const incoming = incomingLinksForDoc(doc);
  const explicit = incoming.filter((link) => link.verknuepfung_typ === "referenz");
  const base = explicit.length
    ? explicit
    : incoming.filter(
        (link) =>
          link.verknuepfung_typ === "umwandlung" &&
          (link.quell_nummer || link.quell_dokument_id),
      );
  const assigned = incoming.filter(
    (link) =>
      link.verknuepfung_typ === "zuordnung" &&
      (link.quell_nummer || link.quell_dokument_id),
  );
  return mergeReferenzLinks(base, assigned);
}

export function selectedReferenzDokumentIds(
  doc: Pick<DokumentDetail, "id" | "verknuepfungen">,
): number[] {
  const ids: number[] = [];
  for (const link of resolveReferenzLinks(doc)) {
    if (!ids.includes(link.quell_dokument_id)) ids.push(link.quell_dokument_id);
  }
  return ids;
}

export function formatReferenzdokumentLabel(
  doc: Pick<DokumentDetail, "id" | "verknuepfungen">,
): string | null {
  const labels = resolveReferenzLinks(doc).map(
    (link) => (link.quell_nummer || "").trim() || `#${link.quell_dokument_id}`,
  );
  return labels.length ? labels.join(", ") : null;
}

export function formatVerknuepfungLine(link: DokumentVerknuepfung): string {
  const quell = (link.quell_nummer || "").trim() || `#${link.quell_dokument_id}`;
  const quellTyp = link.quell_typ
    ? DOKUMENT_TYP_LABELS[link.quell_typ as DokumentTyp] || link.quell_typ
    : "";
  const typ = verknuepfungTypLabel(link.verknuepfung_typ);
  return [quell, quellTyp, typ].filter(Boolean).join(" · ");
}

export function listLinkedAuftragsbestaetigungen(
  verknuepfungen: DokumentVerknuepfung[],
  angebotId: number,
): DokumentVerknuepfung[] {
  return verknuepfungen.filter(
    (link) =>
      link.quell_dokument_id === angebotId &&
      link.ziel_typ === "auftragsbestaetigung" &&
      link.verknuepfung_typ === "umwandlung",
  );
}

/** Anzeigenummer inkl. optionalem Revisions-Suffix (.1 / .2). */
export function dokumentDisplayNumber(
  doc: Pick<DokumentListItem, "dokument_nummer" | "display_number" | "revision_index">,
): string {
  const explicit = String(doc.display_number || "").trim();
  if (explicit) return explicit;
  const base = String(doc.dokument_nummer || "").trim();
  if (!base) return "Entwurf";
  const revision = Number(doc.revision_index || 0);
  if (Number.isFinite(revision) && revision > 0) return `${base}.${revision}`;
  return base;
}

export function dokumentDisplayTitle(
  doc: Pick<
    DokumentListItem,
    "dokument_nummer" | "display_number" | "revision_index" | "dokument_typ" | "rechnung_art" | "betreff"
  >,
): string {
  const nummer = dokumentDisplayNumber(doc);
  const typ = getDokumentTypLabel(doc);
  const betreff = stripGdRichTextMarkup((doc.betreff || "").trim());
  return betreff ? `${nummer} · ${typ} · ${betreff}` : `${nummer} · ${typ}`;
}

/** Bearbeitbar nur im Status „Entwurf“. */
export function isDokumentEditable(
  doc: Pick<DokumentDetail, "status" | "dokument_typ">,
): boolean {
  if (doc.status === "storniert") return false;
  return doc.status === "entwurf";
}

/**
 * Erneut bearbeiten:
 * - Angebot (erstellt/versendet): neue Revision
 * - andere Typen (nur erstellt, nicht Rechnung): Status zurück auf Entwurf
 */
export function canReopenDokumentForEdit(
  doc: Pick<DokumentDetail, "status" | "dokument_typ" | "has_newer_revision">,
): boolean {
  if (doc.dokument_typ === "rechnung") return false;
  if (doc.has_newer_revision) return false;
  if (doc.dokument_typ === "angebot") {
    return doc.status === "erstellt" || doc.status === "versendet";
  }
  return doc.status === "erstellt";
}

/**
 * Neuer Rechnungsentwurf nach Storno:
 * - stornierte Rechnung, oder
 * - zugehörige Stornorechnung (Backend löst die Quellrechnung auf)
 */
export function canCreateInvoiceDraftFromCancelled(
  doc: Pick<DokumentDetail, "status" | "dokument_typ" | "rechnung_art">,
): boolean {
  if (doc.dokument_typ !== "rechnung") return false;
  if (doc.rechnung_art === "stornorechnung") {
    return doc.status !== "storniert";
  }
  return doc.status === "storniert";
}

const NON_BILLABLE_POSITION_TYPES = new Set([
  "titel",
  "text",
  "freitext",
  "zwischensumme",
  "seitenumbruch",
]);

/** Leere Stornorechnung ohne buchungsfähige Positionen – Nachzug aus Quelle/Hero. */
export function canRepairStornoPositionen(
  doc: Pick<DokumentDetail, "status" | "dokument_typ" | "rechnung_art" | "summe_netto">,
  positions: Array<Pick<DokumentPosition, "position_typ">> = [],
): boolean {
  if (doc.dokument_typ !== "rechnung") return false;
  if (doc.rechnung_art !== "stornorechnung") return false;
  if (doc.status === "storniert" || doc.status === "entwurf") return false;
  const hasBillable = positions.some(
    (p) => !NON_BILLABLE_POSITION_TYPES.has(String(p.position_typ || "").toLowerCase()),
  );
  if (hasBillable) return false;
  // Fallback: Summen 0 und keine Positionen (PDF-Ansicht ohne Positions-State)
  return positions.length === 0 || Math.abs(Number(doc.summe_netto || 0)) < 0.005;
}

/** Angebot: Revision statt Zurücksetzen auf Entwurf. */
export function usesDokumentRevisionForReopen(
  doc: Pick<DokumentDetail, "dokument_typ">,
): boolean {
  return doc.dokument_typ === "angebot";
}

/** PDF-Ansicht statt Editor (alle Status außer Entwurf, sofern PDF verfügbar). */
export function showsDokumentPdfView(doc: Pick<DokumentDetail, "status">): boolean {
  return doc.status !== "entwurf" && canDownloadDokumentPdf(doc);
}

export function canDownloadDokumentPdf(doc: Pick<DokumentDetail, "status">): boolean {
  return doc.status !== "storniert";
}

export function canSendDokumentMail(doc: Pick<DokumentDetail, "status">): boolean {
  return doc.status !== "storniert" && doc.status !== "entwurf";
}

/** Zahlung oder Zahlungsverlauf aus der Liste öffnen (offen und bereits bezahlt). */
export function canOpenDokumentZahlungen(
  doc: Pick<
    DokumentListItem,
    "dokument_typ" | "status" | "quelle" | "hero_document_id" | "zahlungsstatus" | "rechnung_art"
  >,
): boolean {
  if (isStundennachweisListItem(doc)) return false;
  if (isStuecklisteListItem(doc)) return false;
  if (doc.dokument_typ !== "rechnung") return false;
  if (doc.rechnung_art === "stornorechnung") return false;
  if (doc.status === "entwurf" || doc.status === "storniert") return false;
  if (canCaptureDokumentZahlung(doc)) return true;
  return (
    doc.zahlungsstatus === "bezahlt" ||
    doc.zahlungsstatus === "teilbezahlt" ||
    doc.status === "abgerechnet"
  );
}

export function dokumentZahlungButtonLabel(
  doc: Pick<DokumentListItem, "status" | "zahlungsstatus">,
  opts?: { mobile?: boolean },
): string {
  if (doc.zahlungsstatus === "bezahlt" || doc.status === "abgerechnet") return "Zahlungen";
  if (doc.zahlungsstatus === "teilbezahlt") return "Teilzahlung";
  return opts?.mobile ? "Zahlung erfassen" : "Zahlung";
}

/** Zahlung direkt aus der Liste nur bei erstellten/versendeten offenen Rechnungen (inkl. Hero). */
export function canCaptureDokumentZahlung(
  doc: Pick<
    DokumentListItem,
    "dokument_typ" | "status" | "quelle" | "hero_document_id" | "zahlungsstatus" | "rechnung_art"
  >,
): boolean {
  if (isStundennachweisListItem(doc)) return false;
  if (isStuecklisteListItem(doc)) return false;
  if (doc.dokument_typ !== "rechnung") return false;
  if (doc.rechnung_art === "stornorechnung") return false;
  if (doc.status === "abgerechnet" || doc.zahlungsstatus === "bezahlt") return false;
  return doc.status === "erstellt" || doc.status === "versendet";
}

/** Umwandlung in Folgedokumente nur nach dem Erstellen (nicht im Entwurf). */
export function canConvertDokument(
  doc: Pick<DokumentDetail, "status" | "dokument_typ">,
): boolean {
  if (doc.status === "entwurf" || doc.status === "storniert") return false;
  return (CONVERSION_TARGETS[doc.dokument_typ]?.length ?? 0) > 0;
}

export function canViewKalkulationPdf(
  doc: Pick<DokumentDetail, "status" | "dokument_typ">,
): boolean {
  return doc.dokument_typ === "angebot" && doc.status !== "entwurf" && doc.status !== "storniert";
}

export function canViewBeauftragungPdf(
  doc: Pick<
    DokumentDetail,
    "status" | "dokument_typ" | "beauftragung_sharepoint_item_id"
  >,
): boolean {
  return (
    doc.dokument_typ === "angebot" &&
    doc.status === "angenommen" &&
    Boolean(doc.beauftragung_sharepoint_item_id)
  );
}

function buildApiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
}

export function getDokumentPdfUrl(id: number): string {
  return buildApiUrl(`/dokumente/${id}/pdf`);
}

/** Dateiname aus Content-Disposition (RFC 5987 / quoted filename). */
export function filenameFromContentDisposition(
  header: string | null,
  fallback = "Dokument.pdf",
): string {
  if (!header) return fallback;
  const utf8 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim().replace(/^"|"$/g, "")) || fallback;
    } catch {
      /* fall through */
    }
  }
  const plain = /filename\s*=\s*("?)([^";]+)\1/i.exec(header);
  if (plain?.[2]) {
    return plain[2].trim() || fallback;
  }
  return fallback;
}

export async function fetchDokumentPdf(id: number): Promise<Blob> {
  const res = await fetch(getDokumentPdfUrl(id), { credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export async function fetchDokumentSignedPdf(id: number): Promise<Blob> {
  const res = await fetch(getDokumentSignedPdfUrl(id), { credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(messageFromPdfResponse(text, res.status, res.statusText));
  }
  return res.blob();
}

export type DokumentPdfOpenVariant = "angebot" | "kalkulation" | "beauftragung";

export function getDokumentSignedPdfUrl(id: number): string {
  return buildApiUrl(`/dokumente/${id}/pdf/signed-latest`);
}

function messageFromPdfResponse(text: string, status: number, statusText: string): string {
  const trimmed = text.trim();
  if (trimmed.startsWith("{")) {
    try {
      const body = JSON.parse(trimmed) as { detail?: unknown };
      if (typeof body.detail === "string" && body.detail.trim()) return body.detail.trim();
    } catch {
      /* raw text */
    }
  }
  return trimmed || `${status} ${statusText}`;
}

/**
 * PDF über Session laden und im bekannten Viewer öffnen
 * (Electron: System-App, Browser: neuer Tab).
 */
export async function openPdfAtApiUrl(
  url: string,
  filenameHint?: string | null,
): Promise<void> {
  const { openFileInDefaultApp, isElectronWithOpenTempFile } = await import("@/lib/electron");
  const resolved = buildApiUrl(url);
  const fallbackName = (filenameHint || "Dokument.pdf").replace(/[<>:"/\\|?*\x00-\x1f]/g, "_");
  const safeFallback = fallbackName.toLowerCase().endsWith(".pdf")
    ? fallbackName
    : `${fallbackName}.pdf`;

  const res = await fetch(resolved, { credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(messageFromPdfResponse(text, res.status, res.statusText));
  }
  const filename = filenameFromContentDisposition(
    res.headers.get("Content-Disposition"),
    safeFallback,
  );
  const raw = await res.blob();
  const blob = raw.type === "application/pdf" ? raw : new Blob([raw], { type: "application/pdf" });

  if (isElectronWithOpenTempFile()) {
    await openFileInDefaultApp(blob, filename);
    return;
  }

  const objectUrl = URL.createObjectURL(blob);
  const opened = window.open(objectUrl, "_blank", "noopener");
  if (!opened) {
    URL.revokeObjectURL(objectUrl);
    await openFileInDefaultApp(blob, filename);
    return;
  }
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

/**
 * PDF öffnen ohne blob:-URLs (Electron: System-App, Browser: API-URL in neuem Tab).
 * Unterschriebene PDFs werden per Session geladen und im System-Viewer geöffnet.
 */
export async function openDokumentPdf(
  id: number,
  options?: {
    variant?: DokumentPdfOpenVariant | "signed";
    filenameHint?: string | null;
  },
): Promise<void> {
  const variant = options?.variant ?? "angebot";
  if (variant === "signed") {
    await openPdfAtApiUrl(getDokumentSignedPdfUrl(id), options?.filenameHint);
    return;
  }
  const { openFileInDefaultApp, isElectronWithOpenTempFile } = await import("@/lib/electron");
  const url =
    variant === "kalkulation"
      ? getDokumentKalkulationPdfUrl(id)
      : variant === "beauftragung"
        ? getDokumentBeauftragungPdfUrl(id)
        : getDokumentPdfUrl(id);
  const fallbackName = (options?.filenameHint || "Dokument.pdf").replace(/[<>:"/\\|?*\x00-\x1f]/g, "_");
  const safeFallback = fallbackName.toLowerCase().endsWith(".pdf")
    ? fallbackName
    : `${fallbackName}.pdf`;

  if (isElectronWithOpenTempFile()) {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(messageFromPdfResponse(text, res.status, res.statusText));
    }
    const filename = filenameFromContentDisposition(
      res.headers.get("Content-Disposition"),
      safeFallback,
    );
    const blob = await res.blob();
    await openFileInDefaultApp(blob, filename);
    return;
  }

  window.open(url, "_blank", "noopener");
}

export function getHeroDokumentPdfUrl(heroDocumentId: number): string {
  return buildApiUrl(`/dokumente/hero/${heroDocumentId}/pdf`);
}

export async function fetchHeroDokumentPdf(heroDocumentId: number): Promise<Blob> {
  const res = await fetch(getHeroDokumentPdfUrl(heroDocumentId), { credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

/** Aktiviert ein Hero-Dokument als App-Geschäftsdokument (idempotent). */
export async function activateHeroDokument(heroDocumentId: number): Promise<DokumentDetail> {
  return api.post<DokumentDetail>(`/dokumente/hero/${heroDocumentId}/aktivieren`);
}

export function getDokumentKalkulationPdfUrl(id: number): string {
  return buildApiUrl(`/dokumente/${id}/kalkulation/pdf`);
}

export async function fetchDokumentKalkulationPdf(id: number): Promise<Blob> {
  const res = await fetch(getDokumentKalkulationPdfUrl(id), { credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export function getDokumentBeauftragungPdfUrl(id: number): string {
  return buildApiUrl(`/dokumente/${id}/beauftragung/pdf`);
}

export async function fetchDokumentBeauftragungPdf(id: number): Promise<Blob> {
  const res = await fetch(getDokumentBeauftragungPdfUrl(id), { credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export function getDokumentXrechnungUrl(id: number): string {
  return buildApiUrl(`/dokumente/${id}/xrechnung`);
}

export async function fetchDokumentXrechnung(id: number): Promise<Blob> {
  const res = await fetch(getDokumentXrechnungUrl(id), { credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export type GdPreviewPage = "erste" | "folge";

export async function fetchDokumentPreviewHtml(
  id: number,
  positionen: DokumentPosition[],
  previewPage: GdPreviewPage = "erste",
  signal?: AbortSignal,
  doc?: Record<string, unknown>,
): Promise<string> {
  const res = await fetch(buildApiUrl(`/dokumente/${id}/preview`), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ preview_page: previewPage, positionen, doc }),
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.text();
}

/** PDF-Livevorschau – dieselbe WeasyPrint-Pipeline wie das fertige Dokument. */
export async function fetchDokumentPreviewPdf(
  id: number,
  positionen: DokumentPosition[],
  signal?: AbortSignal,
  doc?: Record<string, unknown>,
): Promise<Blob> {
  const res = await fetch(buildApiUrl(`/dokumente/${id}/preview-pdf`), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ positionen, doc }),
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

/** Varianten für App-Drucker (kurzlebige öffentliche Print-URL). */
export type DokumentPrintVariant =
  | "dokument"
  | "kalkulation"
  | "beauftragung"
  | "mahnung"
  | "hero";

export interface DokumentPrintUrlResponse {
  url: string;
  expires_at: number;
}

export function mapPdfOpenVariantToPrintVariant(
  variant?: DokumentPdfOpenVariant | "signed" | null,
): DokumentPrintVariant {
  if (variant === "kalkulation") return "kalkulation";
  if (variant === "beauftragung") return "beauftragung";
  // Unterschriebenes PDF wird nicht über den App-Drucker gedruckt → erstelltes Dokument.
  return "dokument";
}

export async function getDokumentPrintUrl(
  docId: number,
  options?: { variant?: DokumentPrintVariant; stufe?: string },
): Promise<DokumentPrintUrlResponse> {
  const params = new URLSearchParams();
  params.set("variant", options?.variant ?? "dokument");
  if (options?.stufe) params.set("stufe", options.stufe);
  return api.get<DokumentPrintUrlResponse>(
    `/dokumente/${encodeURIComponent(String(docId))}/pdf/print-url?${params.toString()}`,
    { cache: "no-store" },
  );
}

export async function getHeroDokumentPrintUrl(
  heroDocumentId: number,
): Promise<DokumentPrintUrlResponse> {
  return api.get<DokumentPrintUrlResponse>(
    `/dokumente/hero/${encodeURIComponent(String(heroDocumentId))}/pdf/print-url`,
    { cache: "no-store" },
  );
}

export async function getDokumentPreviewPrintUrl(
  docId: number,
  body: { positionen?: unknown; doc?: Record<string, unknown> | null },
): Promise<DokumentPrintUrlResponse> {
  return api.post<DokumentPrintUrlResponse>(
    `/dokumente/${encodeURIComponent(String(docId))}/preview-pdf/print-url`,
    body,
  );
}

export async function printDokumentViaLocation(
  docId: number,
  locationId: number,
  copies: number,
  options?: { variant?: DokumentPrintVariant; stufe?: string },
): Promise<void> {
  const { setDefaultPrintLocation } = await import("@/lib/printPreferences");
  const { runPrintByLocationWithFeedback } = await import("@/lib/printJob");
  await setDefaultPrintLocation(locationId);
  const { url } = await getDokumentPrintUrl(docId, options);
  await runPrintByLocationWithFeedback(locationId, copies, url, {
    loadingMessage: "Dokument wird gedruckt…",
  });
}

export async function printHeroDokumentViaLocation(
  heroDocumentId: number,
  locationId: number,
  copies: number,
): Promise<void> {
  const { setDefaultPrintLocation } = await import("@/lib/printPreferences");
  const { runPrintByLocationWithFeedback } = await import("@/lib/printJob");
  await setDefaultPrintLocation(locationId);
  const { url } = await getHeroDokumentPrintUrl(heroDocumentId);
  await runPrintByLocationWithFeedback(locationId, copies, url, {
    loadingMessage: "Dokument wird gedruckt…",
  });
}

export async function printDokumentPreviewViaLocation(
  docId: number,
  locationId: number,
  copies: number,
  body: { positionen?: unknown; doc?: Record<string, unknown> | null },
): Promise<void> {
  const { setDefaultPrintLocation } = await import("@/lib/printPreferences");
  const { runPrintByLocationWithFeedback } = await import("@/lib/printJob");
  await setDefaultPrintLocation(locationId);
  const { url } = await getDokumentPreviewPrintUrl(docId, body);
  await runPrintByLocationWithFeedback(locationId, copies, url, {
    loadingMessage: "Dokumentvorschau wird gedruckt…",
  });
}

export interface DokumentMailPrimaryAttachment {
  filename: string;
  content_type: string;
  label: string;
  kind?: string;
}

export interface DokumentMailRelatedDocument {
  id: number;
  dokument_nummer?: string | null;
  dokument_typ: DokumentTyp | string;
  status?: DokumentStatus | string;
  betreff?: string | null;
  label: string;
  /** Nur Lieferscheine: true = unterschriebenes PDF, false = erstelltes PDF ohne Unterschrift. */
  has_signed_pdf?: boolean;
}

export interface DokumentMailSuggestedTimesheet {
  id: number;
  timesheet_id?: number;
  dokument_typ?: string;
  dokument_nummer?: string | null;
  label: string;
  filename?: string | null;
  has_pdf?: boolean;
  has_signed_pdf?: boolean;
  suggested?: boolean;
}

export interface DokumentMailSuggestedLieferschein {
  id: number;
  dokument_id?: number;
  dokument_typ?: string;
  dokument_nummer?: string | null;
  label: string;
  filename?: string | null;
  has_pdf?: boolean;
  has_signed_pdf?: boolean;
  suggested?: boolean;
}

export interface DokumentMailRecipientSuggestion {
  email: string;
  label?: string | null;
  source?: string | null;
}

export interface DokumentMailCompose {
  to: string;
  cc?: string;
  subject: string;
  html_body: string;
  versand_rechnungsformat?: Rechnungsformat;
  /** Kundenvorgabe: Stundennachweis-PDFs beim Versand vorwählen (ZUGFeRD). */
  stundennachweis_mit_rechnung?: boolean;
  attachment_hint?: string;
  primary_attachment?: DokumentMailPrimaryAttachment;
  suggested_timesheets?: DokumentMailSuggestedTimesheet[];
  suggested_lieferscheine?: DokumentMailSuggestedLieferschein[];
  related_documents?: DokumentMailRelatedDocument[];
  recipient_suggestions?: DokumentMailRecipientSuggestion[];
  auftrag_id?: number | null;
}

export async function composeDokumentMail(
  id: number,
  opts?: { vorlage?: string },
): Promise<DokumentMailCompose> {
  const params = new URLSearchParams();
  if (opts?.vorlage) params.set("vorlage", opts.vorlage);
  const qs = params.toString();
  return api.get<DokumentMailCompose>(`/dokumente/${id}/mail/compose${qs ? `?${qs}` : ""}`);
}

export interface AngebotNachfassDokument {
  id: number;
  dokument_nummer?: string | null;
  display_number?: string | null;
  revision_index?: number | null;
  betreff?: string | null;
  kunde_name?: string | null;
  summe_netto?: number | null;
  summe_brutto?: number | null;
  waehrung?: string | null;
  versendet_am?: string | null;
  /** Letzter erneuter Versand oder letzte Nachfrage. Setzt die Zwei-Wochen-Frist neu. */
  nachgefasst_am?: string | null;
  /** `angebot_nachfass` = Nachfrage aus der Übersicht, `erneut` = Angebot nochmal verschickt. */
  nachgefasst_art?: string | null;
  updated_at?: string | null;
  aging_at?: string | null;
  /** Tage seit dem letzten Versand bzw. der letzten Nachfrage. */
  age_days: number;
  /** true, wenn seit dem letzten Versand bzw. der letzten Nachfrage mehr als 2 Wochen vergangen sind. */
  overdue?: boolean;
}

export interface AngebotNachfassGroup {
  key: string;
  ansprechpartner_id?: number | null;
  name: string;
  email?: string | null;
  telefon?: string | null;
  count: number;
  max_age_days: number;
  angebote: AngebotNachfassDokument[];
}

export interface AngebotNachfassResponse {
  min_days: number;
  /** Ansprechpartner mit mindestens einem Angebot, das wieder älter als 2 Wochen ist. */
  items: AngebotNachfassGroup[];
  /** Ansprechpartner, deren Angebote alle nachgefasst sind und noch keine 2 Wochen warten. */
  followed_up_items?: AngebotNachfassGroup[];
  /** Ansprechpartner mit offenem Angebot, die in ``items`` nicht vorkommen. */
  pending_items?: AngebotNachfassGroup[];
}

export async function listAngeboteNachfass(): Promise<AngebotNachfassResponse> {
  return api.get<AngebotNachfassResponse>("/dokumente/angebote/nachfass");
}

export async function sendDokumentMail(
  id: number,
  payload: {
    to: string;
    subject: string;
    html_body?: string;
    body?: string;
    cc?: string;
    bcc?: string;
    mark_versendet?: boolean;
    versand_art?: string;
    attachments?: Array<{
      filename: string;
      content_type: string;
      content_base64: string;
    }>;
    sharepoint_item_ids?: string[];
    attach_dokument_ids?: number[];
    attach_timesheet_ids?: number[];
  },
): Promise<{ ok: boolean; via?: string; marked_versendet?: boolean }> {
  return api.post(`/dokumente/${id}/send-mail`, payload);
}

export interface GdPositionAttachmentMeta {
  id: number;
  dokument_id: number;
  position_id?: number | null;
  media_key: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_by?: string | null;
  uploaded_at?: string | null;
  sharepoint_item_id?: string | null;
  sharepoint_web_url?: string | null;
}

export interface GdMobileUploadQr {
  url: string;
  expires_at?: string | null;
  qr_data_uri?: string | null;
  position_count?: number;
}

export async function listGdPositionAttachments(docId: number): Promise<GdPositionAttachmentMeta[]> {
  return api.get<GdPositionAttachmentMeta[]>(`/dokumente/${docId}/position-attachments`);
}

export function getGdPositionAttachmentUrl(docId: number, attachmentId: number): string {
  return buildApiUrl(`/dokumente/${docId}/position-attachments/${attachmentId}`);
}

/** Verkleinerte Anzeigequalität für Editor-Raster / Positionskarten. */
export function getGdPositionAttachmentThumbUrl(docId: number, attachmentId: number): string {
  return buildApiUrl(`/dokumente/${docId}/position-attachments/${attachmentId}/thumb`);
}

export async function uploadGdPositionAttachment(
  docId: number,
  payload: { mediaKey: string; positionId?: number | null; file: File },
): Promise<GdPositionAttachmentMeta> {
  const fd = new FormData();
  fd.set("media_key", payload.mediaKey);
  if (payload.positionId != null) fd.set("position_id", String(payload.positionId));
  fd.set("file", payload.file);
  return api.postForm<GdPositionAttachmentMeta>(`/dokumente/${docId}/position-attachments`, fd);
}

export async function attachGdPositionAttachmentFromProjectFoto(
  docId: number,
  payload: { mediaKey: string; positionId?: number | null; fotoId: number },
): Promise<GdPositionAttachmentMeta> {
  return api.post<GdPositionAttachmentMeta>(
    `/dokumente/${docId}/position-attachments/from-project-foto`,
    {
      media_key: payload.mediaKey,
      position_id: payload.positionId ?? null,
      foto_id: payload.fotoId,
    },
  );
}

export async function deleteGdPositionAttachment(
  docId: number,
  attachmentId: number,
): Promise<{ ok: boolean; id: number }> {
  return api.del(`/dokumente/${docId}/position-attachments/${attachmentId}`);
}

export async function getGdMobileUploadQr(docId: number): Promise<GdMobileUploadQr> {
  return api.get<GdMobileUploadQr>(`/dokumente/${docId}/mobile-upload-qr`);
}

export interface HistorischeDokumentPosition {
  quelle?: "app" | "hero" | null;
  position_id: number;
  position_typ: string;
  artikel_id?: number | null;
  bezeichnung: string;
  beschreibung?: string | null;
  hero_nr?: string | null;
  einheit?: string | null;
  menge: number;
  einzelpreis_netto: number;
  rabatt_prozent: number;
  rabatt_betrag?: number;
  rabatt_modus?: "prozent" | "betrag";
  rabatt_ausweis?: "intern" | "dokument";
  steuersatz_prozent: number;
  kalkulation_json?: unknown;
  dokument_id: number;
  dokument_typ?: DokumentTyp | string | null;
  dokument_nummer?: string | null;
  betreff?: string | null;
  datum?: string | null;
  kunde_name?: string | null;
  auftrag_id?: number | null;
  auftrag_nummer?: string | null;
}

export function buildHistorischePositionenSearchParams(options: {
  q?: string;
  kundenId?: number | null;
  ansprechpartnerId?: number | null;
  ordnerId?: number | null;
  sordnerId?: number | null;
  excludeDokumentId?: number | null;
  limit?: number;
}): {
  q?: string;
  kundenId?: number | null;
  ansprechpartnerId?: number | null;
  ordnerId?: number | null;
  sordnerId?: number | null;
  excludeDokumentId?: number | null;
  limit: number;
} {
  return {
    q: options.q,
    kundenId: options.kundenId ?? null,
    ansprechpartnerId: options.ansprechpartnerId ?? null,
    ordnerId: options.ordnerId ?? null,
    sordnerId: options.sordnerId ?? null,
    excludeDokumentId: options.excludeDokumentId ?? null,
    limit: options.limit ?? 100,
  };
}

export async function searchHistorischePositionen(params: {
  q?: string;
  dokumentTyp?: DokumentTyp;
  auftragId?: number | null;
  kundenId?: number | null;
  ansprechpartnerId?: number | null;
  ordnerId?: number | null;
  sordnerId?: number | null;
  excludeDokumentId?: number | null;
  limit?: number;
}): Promise<HistorischeDokumentPosition[]> {
  const qs = buildQuery({
    q: params.q?.trim() || undefined,
    dokument_typ: params.dokumentTyp,
    auftrag_id: params.auftragId ?? undefined,
    kunden_id: params.kundenId ?? undefined,
    ansprechpartner_id: params.ansprechpartnerId ?? undefined,
    ordner_id: params.ordnerId ?? undefined,
    sordner_id: params.sordnerId ?? undefined,
    exclude_dokument_id: params.excludeDokumentId ?? undefined,
    limit: params.limit ?? 100,
  });
  const res = await api.get<{ items: HistorischeDokumentPosition[] }>(
    `/dokumente/quellen/positionen${qs}`,
  );
  return res.items || [];
}

/* ─────────────────────── Tabellen-Settings ─────────────────── */

export interface DocumentColumnView {
  columns: string[];
  widths: Record<string, number>;
}

export interface DocumentColumnSettings {
  desktop: DocumentColumnView | null;
  mobile: DocumentColumnView | null;
}

interface DocumentColumnSettingsResponse {
  table_key: string;
  settings: DocumentColumnSettings | null;
}

const DOCUMENT_TABLE_KEY = "documents";
const DOCUMENT_LOCAL_STORAGE_KEY = "eh_gd_document_table_columns_v1";

function normalizeDocumentSettings(
  settings: DocumentColumnSettings | null | undefined,
): DocumentColumnSettings {
  return {
    desktop: settings?.desktop ?? null,
    mobile: settings?.mobile ?? null,
  };
}

function readLocalDocumentColumnSettings(): DocumentColumnSettings | null {
  try {
    const raw = localStorage.getItem(DOCUMENT_LOCAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DocumentColumnSettings;
    if (!parsed?.desktop && !parsed?.mobile) return null;
    return normalizeDocumentSettings(parsed);
  } catch {
    return null;
  }
}

export async function getDocumentColumnSettings(): Promise<DocumentColumnSettings> {
  const res = await api.get<DocumentColumnSettingsResponse>(
    `/me/table-settings/${DOCUMENT_TABLE_KEY}`,
  );
  const settings = normalizeDocumentSettings(res?.settings);
  if (settings.desktop || settings.mobile) return settings;

  // Einmalige Migration von lokal gespeicherten Spalteneinstellungen.
  const local = readLocalDocumentColumnSettings();
  if (!local?.desktop && !local?.mobile) return settings;
  try {
    const saved = await saveDocumentColumnSettings(local);
    localStorage.removeItem(DOCUMENT_LOCAL_STORAGE_KEY);
    return saved;
  } catch {
    return local;
  }
}

export async function saveDocumentColumnSettings(
  settings: DocumentColumnSettings,
): Promise<DocumentColumnSettings> {
  const res = await api.post<{ ok: true; settings: DocumentColumnSettings }>(
    `/me/table-settings/${DOCUMENT_TABLE_KEY}`,
    { settings },
  );
  return normalizeDocumentSettings(res?.settings ?? settings);
}
